import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  AdsQueue, delay, getDeviceAds, IAdLoop, QueueConfig,
} from './requests';
import { cleanCurrentAd, parseConfig } from '../../utils/helpers';

import {
  axiosConfig, cdnURL, handleQueues, queues,
} from '../../config';

import { Actions, StrapiComponentType, useAdLoopReducer } from './useAdLoopReducer';
import AdPlayer from './AdPlayer';
import PortlTransition from './Transition';
import { AdType, Device } from '../../types';
import { AdsDelay, Log, registerLog } from './api';
import useInterval from './useInterval';
// import { useWeatherNewsComponents } from './useWeatherNewsComponent';
import { useComponentRef } from './useComponentRef';

const AdContainer = styled.div`
    width: 100%;
    height: initial;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: white;
  `;

const BrandedImage = styled.img`
    height: 101%;
    width: 101%;
  `;

type AdLoopProps = {
  device?: Device
};

export const DEFAULT_CONFIG = {
  queueCap: { ads: 5 },
  clientAdRequestIntervals: {
    ms: 2 * 1000,
  },
  weights: {
    server: 8,
    fallback: 1,
    component: 1,
    weather: 1,
    news: 1,
  },
} as Record<string, any>;

const DEFAULT_LOG = {
  pops: 0,
  adsQueued: 0,
  failedPops: 0,
  adRequests: 0,
  latestAdRequest: null,
  adsDelay: 0,
};

const AdLoop = ({ device }: AdLoopProps): React.ReactElement => {
  const [state, dispatch] = useAdLoopReducer();
  // @ts-ignore
  const { organization } = device;

  const adsDelay = useRef<AdsDelay>({ totalDelay: 0, count: 0 });
  const log = useRef<Log>(
    DEFAULT_LOG,
  );

  // only use weather and news if the device is external
  // useWeatherNewsComponents({ device });
  const componentRef = useComponentRef();

  const config = useRef<QueueConfig>(DEFAULT_CONFIG as QueueConfig);

  const triggerPopFlow = async ({
    // @ts-ignore
    ad, onPopInit = () => {}, onPopSuccess = () => {}, onPopError = () => {}, onPopFinally = () => {},
  }) => {
    // console.log('trigger pop flow initiated...');
    try {
      onPopInit();
      const popUrl = ad.pop;
      await axios.get(popUrl, axiosConfig);
      // pops.current += 1;
      log.current.pops += 1;
      // increment({ object: log.current.pops, key: ad.type });
      onPopSuccess();
    } catch (error) {
      log.current.failedPops += 1;
      // increment({ object: log.current.failedPops, key: ad.type });
      console.error('Error Pop', error);
      onPopError();
    } finally {
      onPopFinally();
    }
  };

  const popAds = async () => {
    if (!queues.current || !queues.current.serverQueue.length) return;

    if (!queues.current.poppedAdsQueue) {
      queues.current.poppedAdsQueue = [];
    }

    while (queues.current.serverQueue.length > 0) {
      const ad = queues.current?.serverQueue[0];
      onUpdateQueue(ad, 'serverQueue');

      if (!ad?.pop) {
        queues.current?.poppedAdsQueue.push(ad);
        return;
      }

      // logging programmatic ads
      // compute delay

      const adDelay = new Date().getTime() - ad.createdAt;
      adsDelay.current.count += 1;
      adsDelay.current.totalDelay += adDelay;

      const onPopSuccess = () => {
        queues.current?.poppedAdsQueue.push(ad);
      };

      await triggerPopFlow({ ad, onPopSuccess });
    }
  };

  const getNext = () => {
    if (!queues.current) {
      return;
    }

    popAds();

    const {
      poppedAdsQueue,
    } = queues.current;

    // @ts-ignore
    const adArray: Actions[] = [];
    const pushToAdArray = (count: number, action: Actions) => {
      for (let i = 0; i < count; i += 1) {
        adArray.push(action);
      }
    };

    if (poppedAdsQueue?.length > 0) {
      pushToAdArray(config.current.weights?.[AdType.Server], { type: 'SET_AD', payload: poppedAdsQueue[0] });
    }

    // news and weather logic
    if (!componentRef.current?.news.error) {
      pushToAdArray(config.current.weights?.[AdType.News], { type: 'SET_COMPONENT', payload: StrapiComponentType.News });
    }

    if (!componentRef.current?.weather.error) {
      pushToAdArray(config.current.weights?.[AdType.Weather], { type: 'SET_COMPONENT', payload: StrapiComponentType.Weather });
    }

    // fallback logic
    if (adArray.length === 0) {
      const fallbackSize = device?.width && device?.height && `${device?.width}x${device.height}`;

      const fallbackUrl = fallbackSize ? `${cdnURL}/external/fallbacks/${fallbackSize}.png` : '../assets/black.png';
      const fallbackAd: IAdLoop = {
        duration: 6,
        uniqueId: uuidv4(),
        weight: 1,
        createdAt: new Date().getTime(),
        mediaFile: {
          blobURL: fallbackUrl,
          _type: 'image/png',
        },
        pop: undefined,
        type: AdType.Fallback,

      };
      dispatch({ type: 'SET_AD', payload: fallbackAd });
      // try again in 5s if no ads are found
      setTimeout(() => {
        getNext();
      }, 5000);
      return;
    }

    const weightedDispatchIndex = Math.floor(Math.random() * adArray.length);
    dispatch(adArray[weightedDispatchIndex]);
  };

  const getDeviceAd = async () => {
    if ((queues.current?.poppedAdsQueue?.length || 0) >= config.current.queueCap.ads) { return; }

    try {
      const ads = await getDeviceAds({ partnerName: organization });

      if (ads.length === 0 || !queues.current) return;

      ads.forEach((ad) => {
        queues.current?.serverQueue.push(ad);
        log.current.adsQueued += 1;
      });
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    }
  };

  // const mounted = useRef(false);
  const controller = new AbortController();
  const { signal } = controller;

  const startProviderLoop = async () => {
    while (!signal.aborted) {
      log.current.latestAdRequest = Date.now();
      await getDeviceAd();
      await delay(config.current.clientAdRequestIntervals.ms || 20 * 1000);
      // await delay(1000);
    }
  };

  const fetchInitQueues = async () => {
    dispatch({ type: 'SET_LOADING' });

    // setting up config
    await parseConfig(config);

    dispatch({ type: 'TOGGLE_LOADING', payload: false });
    startProviderLoop();

    handleQueues({
      serverQueue: [],
      // fallbackQueue: [],
      weatherQueue: [],
      newsQueue: [],
    });
    // handleQueues(ads);
    getNext();
  };

  const onUpdateQueue = (ad: IAdLoop, queueKey: keyof AdsQueue) => {
    if (queues.current) {
      (queues.current[queueKey] as IAdLoop[]) = cleanCurrentAd(ad, queues.current?.[queueKey] as IAdLoop[]);
    }
  };

  const onAdEnded = () => {
    console.log('ended');
    onUpdateQueue(state.ad, 'poppedAdsQueue');
    getNext();
  };

  const onAdError = () => {
    if (state.error || state.loading || !queues.current) {
      return;
    }

    onUpdateQueue(state.ad, 'poppedAdsQueue');
    getNext();
  };

  // abort on unmount
  useEffect(() => () => {
    controller.abort();
    // eslint-disable-next-line
  }, []);
  //
  useEffect(() => {
    if (!device) return;

    fetchInitQueues();
    // eslint-disable-next-line
  }, [device]);

  useEffect(() => {
    if (!state.error) {
      return;
    }

    console.log('Resetting ad loop...');

    const timerId = setTimeout(() => {
      fetchInitQueues();
    }, 20 * 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.error]);
  //
  useInterval(() => {
    const computeAverageDelayPerProvider = () => {
      const { totalDelay, count } = adsDelay.current;
      log.current.adsDelay = Math.floor(totalDelay / count);
    };

    computeAverageDelayPerProvider();
    registerLog(log.current);

    log.current = DEFAULT_LOG;
    adsDelay.current = { totalDelay: 0, count: 0 };
  }, 5 * 60 * 1000);
  //

  if (state.loading) {
    return (
      <AdContainer>
        <LoadingSpinner />
      </AdContainer>
    );
  }

  if (state.error) {
    return (
      <AdContainer>
        <BrandedImage
          src={`${cdnURL}/external/fallbacks/432x432.png`}
          alt='Error'
        />
        {/* <Logo src='././logo.webp' /> */}
      </AdContainer>
    );
  }

  // return <></>;

  return (
    <AdContainer>
      {/* {!isProduction && <AdCounter popsCount={pops.current} stateCounts={counts.current} />} */}
      <PortlTransition>
        <AdPlayer
          mediaFile={state.ad?.mediaFile}
          duration={state.ad?.duration}
          onEnded={onAdEnded}
          onError={onAdError}
          componentProps={{ componentType: state.component, component: componentRef.current[state.component] }}
        />
      </PortlTransition>
    </AdContainer>
  );
};

export default AdLoop;
